import React, { useState, useContext, useEffect } from "react";
import HeadingText from "components/common/HeadingText/HeadingText";
import ButtonComponent from "components/common/Button/ButtonComponent";
import { Col, Container, Form, Row } from "reactstrap";
import SearchableSelect from "components/common/SearchableSelect/SearchableSelect";
import FormSectionLayout from "components/layouts/FormSectionLayout";
import {
  IAddUpdateMetrics,
  ICreateUpdateMetrics,
  IIdValuePairResponse,
  IMultiSelectInterface,
} from "interfaces/Index";
import Toaster from "components/common/Toaster/Toaster";
import LoaderComponent from "components/common/Loader/Loader";
import { AbilityContext, Can } from "../../../config/Can";
import MultiSelectDropDown from "components/common/MultiSelectDropDown/MultiSelectDropDown";
import {
  normalizeCoreMetrics,
  normalizeMetricsCreateUpdatPayload,
} from "normalizer/MetricsNormalizer";
import UploadButtonComponent from "components/common/UploadButton/UploadButtonComponent";
import "./Metrics.scss";
import { normalizeBulkUploadPayload } from "normalizer/GlobalProductNormalizer";
import Modal from "components/common/Modal/Modal";
import { acceptableFormats } from "config/constants";

const MetricsPage: React.FunctionComponent<IAddUpdateMetrics> = (
  props: IAddUpdateMetrics
) => {
  const ability = useContext(AbilityContext);
  const [metrics, setMetrics] = useState<ICreateUpdateMetrics>({
    cityId: props.metricsDetailsFetched && props.metricsDetailsFetched.cityId,
    categoryMetrics:
      props.metricsDetailsFetched &&
      props.metricsDetailsFetched.categoryMetrics,
    coreMetrics:
      props.metricsDetailsFetched && props.metricsDetailsFetched.coreMetrics,
  });

  const [selectedCity, setSelectedCity] = useState<IIdValuePairResponse | null>(
    props.citiesList[0]
  );

  const [MetricAndCatrgoriesUpdated, setMetricAndCatrgoriesUpdated] = useState({
    CoreMetricsUpdated: false,
    CategoryMetrics: false,
    UploadMetrics: null,
    UploadMetricsFileName: "",
  });

  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });
  const [showMetricsModal, setIsShowMetricsModal] = useState(false);

  useEffect(() => {
    const { error, isError } = props;
    if (isError) {
      handleToaster(true, (error! as any).errors);
      alert((error! as any).errors);
    }
  }, [selectedCity, props.isError]);

  useEffect(() => {
    const { fetchCities } = props;
    fetchCities();
  }, []);

  useEffect(() => {
    const { metricsDetailsFetched, isMetricsDetailsFetched } = props;
    isMetricsDetailsFetched &&
      metricsDetailsFetched &&
      handleCoreMetricsMultiSelectResponse();
  }, [
    props.isCoreMetricsFetched,
    props.coreMetricsFetched,
    props.isCategoriesMetricsFetched,
    props.categoriesMetrics,
    props.isMetricsDetailsFetched,
    props.metricsDetailsFetched,
  ]);

  useEffect(() => {
    const { isMetricsDetailsFetched, categoriesMetrics } = props;
    isMetricsDetailsFetched && categoriesMetrics && handleCategoriesMetrics();
  }, [props.coreMetricsFetched && props.categoriesMetrics]);

  useEffect(() => {
    const { getMetrics, getCategoriesForMetrics, getAllCoreMetrics } = props;
    handleCategoriesMetrics();
    if (selectedCity) {
      getMetrics(selectedCity.id);
      getCategoriesForMetrics(selectedCity.id);
    }
    getAllCoreMetrics();
    props.isMetricsUpdated && handleToaster(false, "Metrics has been updated");
  }, [props.isMetricsUpdated]);

  const handleCategoriesMetrics = () => {
    const categoriesMetrics = props.categoriesMetrics;
    const metricsDetailsFetched: IMultiSelectInterface[] = normalizeCoreMetrics(
      props.metricsDetailsFetched && props.metricsDetailsFetched.categoryMetrics
    );
    const sliceMetrics = metricsDetailsFetched;

    const t = sliceMetrics?.map((metric: any, index: number) => {
      const metricIdMatched = categoriesMetrics.findIndex((i) => {
        return i.id === metric.id;
      });

      sliceMetrics[index] =
        categoriesMetrics && categoriesMetrics[metricIdMatched];
    });

    setMetrics({
      ...metrics,
      categoryMetrics: sliceMetrics,
    });
  };

  useEffect(() => {
    const { getMetrics, getCategoriesForMetrics, getAllCoreMetrics } = props;
    if (selectedCity) {
      getMetrics(selectedCity.id);
      getCategoriesForMetrics(selectedCity.id);
      getAllCoreMetrics();
    }
    if (!selectedCity) {
      handleSelectedCityChange(props.citiesList[0]);
    } else {
      handleSelectedCityChange(selectedCity);
    }
  }, [props.citiesList, selectedCity]);

  const validate = selectedCity?.id && metrics.coreMetrics;

  const handleSelectedCityChange = (selectedCity: IIdValuePairResponse) => {
    setSelectedCity(selectedCity);
    setMetrics({
      ...metrics,
      coreMetrics: [],
      cityId: selectedCity?.id,
    });
  };

  const handleCoreMetricsMultiSelectResponse = () => {
    const { isMetricsDetailsFetched, metricsDetailsFetched } = props;
    if (isMetricsDetailsFetched && metricsDetailsFetched) {
      let result = normalizeCoreMetrics(metricsDetailsFetched.coreMetrics);
      setMetrics({
        ...metrics,
        coreMetrics: result,
      });
    }
  };

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
    setTimeout(() => {
      setToaster({
        isVisible: false,
        message: "",
        isError: false,
      });
    }, 3000);
  };

  const { coreMetrics, categoryMetrics } = metrics;

  const handleCoreMetrics = (e: IMultiSelectInterface[]) => {
    setMetricAndCatrgoriesUpdated({
      ...MetricAndCatrgoriesUpdated,
      CoreMetricsUpdated: true,
    });
    setMetrics({
      ...metrics,
      coreMetrics: e,
    });
  };

  const handleCategoriesForMetrics = (e: IMultiSelectInterface[]) => {
    setMetricAndCatrgoriesUpdated({
      ...MetricAndCatrgoriesUpdated,
      CategoryMetrics: true,
    });
    metrics.categoryMetrics = e;
    setMetrics({
      ...metrics,
    });
  };

  const handleSubmit = () => {
    const { updateMetrics, metricsUpload } = props;
    const { CategoryMetrics, CoreMetricsUpdated, UploadMetrics } =
      MetricAndCatrgoriesUpdated;
    if (UploadMetrics && (CategoryMetrics || CoreMetricsUpdated)) {
      const response = normalizeMetricsCreateUpdatPayload(metrics);
      updateMetrics(response);
      const normalizedMetric = normalizeBulkUploadPayload(UploadMetrics);
      metricsUpload(normalizedMetric, { cityId: selectedCity?.id });
      setIsShowMetricsModal(true);
    } else if (UploadMetrics) {
      const normalizedMetric = normalizeBulkUploadPayload(UploadMetrics);
      metricsUpload(normalizedMetric, { cityId: selectedCity?.id });
      setIsShowMetricsModal(true);
    } else if (CategoryMetrics || CoreMetricsUpdated) {
      const response = normalizeMetricsCreateUpdatPayload(metrics);
      updateMetrics(response);
    }
  };

  const closeModal = () => {
    setIsShowMetricsModal(false);
  };

  const handleUploadMetricsCsv = (file: any) => {
    const { metricsUpload } = props;
    if (file) {
      setMetricAndCatrgoriesUpdated({
        ...MetricAndCatrgoriesUpdated,
        UploadMetrics: file,
        UploadMetricsFileName: file.name,
      });
    }
  };

  const renderUploadMetrics = () => {
    return (
      <FormSectionLayout subTitleText="Targets">
        <section className="form-group search-container">
          <Row id="metrics-csv">
            <Col>
              <UploadButtonComponent
                id="uploadMetricsCsv"
                buttonText="Upload .Csv"
                fileFormat={acceptableFormats.csv}
                handleFileChange={handleUploadMetricsCsv}
                classes="button-large"
              />
              <a
                href={require("../../../assets/static/Agent_Targets_CSV.csv")}
                target="_blank"
                className="download-sample-file"
              >
                Download Sample File
              </a>
            </Col>
            <Col>{MetricAndCatrgoriesUpdated.UploadMetricsFileName}</Col>
          </Row>
        </section>
        {renderFormButton()}
      </FormSectionLayout>
    );
  };

  const renderCities = () => {
    return (
      <>
        {props.isLoading && <LoaderComponent />}
        <Modal
          modalHeading="Upload metrics"
          showModal={showMetricsModal}
          modalLoading={props.isMetricsUploading}
          isSuccess={props.isMetricsUploaded}
          showProgressString={props.isMetricsUploadingError ? false : true}
          failedRecords={props.isMetricsUploadingError ? "" : ""}
          closeModalHandler={closeModal}
          modalBodyClass="add-metrics-modal"
        >
          {props.isMetricsUploadingError ? (
            <div className="metrics-modal-body">
              <section className="upload-error">
                <img src="../../../assets/images/warning.svg" />
                <div className="title">Upload failed</div>
              </section>

              {props.isMetricsUploadingError ? (
                <div className="desc">
                  {props.error && (props.error! as any).errors}
                </div>
              ) : (
                <div className="desc">
                  "There is a problem from our side. Please try again later."
                </div>
              )}
            </div>
          ) : null}
        </Modal>

        <Toaster
          showToaster={showToaster.isVisible}
          toasterClass={
            showToaster.isError
              ? "toaster toaster-bg-danger"
              : "toaster toaster-bg-success"
          }
        >
          {showToaster.message || ""}
        </Toaster>
        <FormSectionLayout subTitleText="City">
          <SearchableSelect
            label="city"
            options={props.citiesList}
            id="city"
            onChange={handleSelectedCityChange}
            selectedValue={selectedCity}
            placeholder="Select city"
            isSearchable={false}
          />
        </FormSectionLayout>
      </>
    );
  };

  const coreCityMetrics = () => {
    return (
      <FormSectionLayout subTitleText="Core Metrics">
        <MultiSelectDropDown
          options={props.coreMetricsFetched}
          label="Core metrics"
          selectedValues={coreMetrics}
          placeholder="Please select core metrics"
          displayValue="name"
          onChange={handleCoreMetrics}
          onRemove={handleCoreMetrics}
        />
      </FormSectionLayout>
    );
  };

  const categoriesForMetrics = () => {
    return (
      <FormSectionLayout subTitleText="Categories">
        <MultiSelectDropDown
          options={props.categoriesMetrics}
          label="Categories"
          selectedValues={categoryMetrics}
          placeholder="Please select categories"
          displayValue="name"
          onChange={handleCategoriesForMetrics}
          onRemove={handleCategoriesForMetrics}
          className="categoryMetrics"
        />
      </FormSectionLayout>
    );
  };

  const renderFormButton = () => {
    return (
      <Row className="mt-5">
        <Col lg="6">
          <div>
            {ability.can("*", "agent") && (
              <ButtonComponent
                buttonClass="small-button btn-primary submit-metrics"
                buttonText="Save"
                onClickHandler={handleSubmit}
                isDisable={props.isLoading || !validate}
              />
            )}
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <Container fluid className="metrics-container">
      <Row>
        <Col lg="5">
          <div className="metrics-main-heading-margin">
            <HeadingText text={"Metrics"} isMainHeading={true} />
          </div>
        </Col>
      </Row>

      <Row className="metrics-form-container">
        <Col xs="12">
          <Form>
            <div className="metrics-cities">{renderCities()}</div>
            <div className="metrics-core">{coreCityMetrics()}</div>
            <div className="metrics-categories">{categoriesForMetrics()}</div>
            <div className="metrics-upload">{renderUploadMetrics()}</div>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default MetricsPage;
